import React, { useState } from 'react';
import { ChevronDown, Check, Globe } from 'lucide-react';
import { CURRENCY_SYMBOLS, CURRENCY_NAMES } from '@/hooks/useCurrency';

const CurrencySelector = ({ 
  currency, 
  onSelect, 
  availableCurrencies = Object.keys(CURRENCY_SYMBOLS),
  compact = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (curr) => {
    onSelect(curr);
    setIsOpen(false);
  };

  if (compact) {
    return (
      <div className="relative inline-block">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="currency-selector-btn"
        >
          <Globe className="w-4 h-4" />
          <span>{currency}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-xl border border-slate-200 z-50 py-1 max-h-64 overflow-y-auto">
              {availableCurrencies.map((curr) => (
                <button
                  key={curr}
                  onClick={() => handleSelect(curr)}
                  className={`w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors ${
                    currency === curr ? 'bg-slate-50' : ''
                  }`}
                  data-testid={`currency-option-${curr}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-slate-500 w-8">{CURRENCY_SYMBOLS[curr]}</span>
                    <span className="text-slate-700">{curr}</span>
                  </span>
                  {currency === curr && <Check className="w-4 h-4 text-emerald-500" />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
        data-testid="currency-selector-btn"
      >
        <Globe className="w-5 h-5 text-slate-500" />
        <span className="font-medium">{CURRENCY_SYMBOLS[currency]} {currency}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-50 py-2 max-h-80 overflow-y-auto">
            <div className="px-3 py-2 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100 mb-1">
              Выберите валюту
            </div>
            {availableCurrencies.map((curr) => (
              <button
                key={curr}
                onClick={() => handleSelect(curr)}
                className={`w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors ${
                  currency === curr ? 'bg-sky-50' : ''
                }`}
                data-testid={`currency-option-${curr}`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-lg font-medium text-slate-700 w-10">{CURRENCY_SYMBOLS[curr]}</span>
                  <span className="text-sm text-slate-600">{CURRENCY_NAMES[curr] || curr}</span>
                </span>
                {currency === curr && <Check className="w-5 h-5 text-sky-500" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CurrencySelector;
